// src/components/send/SendTab.jsx
// 발송 탭: 예약 정보 입력 → 메시지 종류 선택 → 수정 → 메시지 보내기

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { parseOTAText, is050Number } from '../../utils/otaParser';
import { extractTextFromImage } from '../../services/visionApi';
import {
  MESSAGE_TEMPLATES,
  OPERATION_TEMPLATE_IDS,
  buildMessage,
  sendMessage,
  isParseSuccess,
} from '../../services/messageTemplates';
import { useApp } from '../../context/AppContext';
import { fsLogParsingEvent } from '../../services/firebase';

// ─── 야놀자 샘플 텍스트 ───
const SAMPLE_OTA_TEXT = `[야놀자] 예약 확정 안내

예약번호 : YN-20260715-8834
예약자 : 김펜션
연락처 : 010-1111-1111
객실 : 힐링독채 A
체크인 : 2026.07.15
체크아웃 : 2026.07.16
인원 : 성인:4명/아동:0명/유아:0명
결제금액 : 220,000원

즐거운 여행 되세요 :)`;

// ─── 신뢰도 아이콘 ───
function ConfidenceIcon({ level }) {
  if (level === 'high') return <span style={{ color: 'var(--green)', fontSize: 12 }}>● 확정</span>;
  if (level === 'medium') return <span style={{ color: 'var(--amber)', fontSize: 12 }}>● 추정</span>;
  if (level === 'guess') return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>● 추측</span>;
  return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>○ 미감지</span>;
}

// ─── 편집 가능한 필드 ───
function EditableField({ label, value, onChange, confidence, type = 'text', placeholder }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="parse-field-row">
      <span className="parse-field-label">{label}</span>
      {editing ? (
        <input
          className="input"
          style={{ height: 34, padding: '0 10px', fontSize: 14, flex: 1 }}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          autoFocus
        />
      ) : (
        <>
          <span className="parse-field-value" style={{ color: value ? 'var(--primary)' : 'var(--text-muted)' }}>
            {value || <em style={{ fontStyle: 'normal', color: 'var(--text-muted)' }}>{placeholder || '미감지'}</em>}
            {label === '전화번호' && is050Number(value) && (
              <span className="safe-number-badge" style={{ marginLeft: 6 }}>🔒 안심번호</span>
            )}
          </span>
          <ConfidenceIcon level={confidence} />
          <button
            onClick={() => setEditing(true)}
            style={{ border: 'none', background: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 4px', fontSize: 12 }}
          >
            수정
          </button>
        </>
      )}
    </div>
  );
}

// ─── 스크린샷 업로더 ───
const STEP = { IDLE: 'idle', LOADING: 'loading', ERROR: 'error' };

function ScreenshotUploader({ onParsed }) {
  const [image, setImage] = useState(null);       // { file, url }
  const [step, setStep] = useState(STEP.IDLE);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  // Object URL 메모리 누수 방지: image 교체 및 컴포넌트 언마운트 시 해제
  useEffect(() => {
    return () => {
      if (image?.url) URL.revokeObjectURL(image.url);
    };
  }, [image]);

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(prev => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return { file, url: URL.createObjectURL(file) };
    });
    setStep(STEP.IDLE);
    setErrorMsg('');
  }

  function clearImage() {
    setImage(prev => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setStep(STEP.IDLE);
    setErrorMsg('');
  }

  async function handleExtract() {
    if (!image) return;
    setStep(STEP.LOADING);
    setErrorMsg('');
    try {
      const text = await extractTextFromImage(image.file);
      const result = parseOTAText(text);
      const formValues = {
        name: result.name || '',
        phone: result.phone || '',
        checkin: result.checkin || '',
        checkout: result.checkout || '',
        guests: result.guests || '',
        channel: result.channel || '',
      };
      setStep(STEP.IDLE);
      onParsed(result, formValues);
    } catch (err) {
      setStep(STEP.ERROR);
      if (err.message === 'NO_API_KEY') {
        setErrorMsg('이미지 분석 기능이 아직 설정되지 않았습니다. 관리자에게 문의해주세요.');
      } else if (err.message === 'NO_TEXT') {
        setErrorMsg('이미지에서 글자를 찾지 못했습니다. 선명한 스크린샷을 다시 올려주세요.');
      } else {
        setErrorMsg('이미지를 분석하는 중 오류가 생겼습니다. 다시 시도해주세요.');
      }
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* 업로드 영역 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {!image ? (
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: '100%', padding: '28px 16px',
            border: '2px dashed var(--green-border)',
            borderRadius: 12, background: 'var(--green-light)',
            cursor: 'pointer', textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 8 }}>🖼️</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--green-dark)', marginBottom: 6 }}>
            예약 화면 올리면 자동으로 입력됩니다
          </div>
          <div style={{ fontSize: 12, color: '#15803d', fontWeight: 600, marginBottom: 5 }}>
            이름 · 날짜 · 객실이 자동으로 입력됩니다
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            야놀자 / 여기어때 / 네이버 예약 화면을 캡처해서 올려주세요
          </div>
        </button>
      ) : (
        <div>
          {/* 이미지 미리보기 */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <img
              src={image.url}
              alt="업로드한 스크린샷"
              style={{ width: '100%', borderRadius: 10, border: '1.5px solid var(--border)', maxHeight: 220, objectFit: 'cover', objectPosition: 'top' }}
            />
            <button
              onClick={clearImage}
              style={{
                position: 'absolute', top: 8, right: 8,
                width: 28, height: 28, borderRadius: '50%',
                border: 'none', background: 'rgba(0,0,0,0.55)',
                color: 'white', fontSize: 16, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>

          {/* 이미지 변경 링크 */}
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', marginBottom: 10, padding: 0 }}
          >
            다른 이미지로 변경
          </button>
        </div>
      )}

      {/* 오류 메시지 */}
      {step === STEP.ERROR && (
        <div style={{ background: '#fef2f2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 8 }}>
          {errorMsg}
        </div>
      )}

      {/* 분석 버튼 */}
      <button
        className="btn btn-green btn-block"
        style={{ marginTop: 10, padding: '13px' }}
        onClick={handleExtract}
        disabled={!image || step === STEP.LOADING}
      >
        {step === STEP.LOADING ? (
          <><div className="spinner" style={{ display: 'inline-block', marginRight: 8 }} />이미지에서 예약 정보 불러오는 중...</>
        ) : (
          '👉 예약 정보 자동 입력하기'
        )}
      </button>
    </div>
  );
}

// ─── 퇴실 후 행동 기록 ───
const BEHAVIOR_ITEMS = [
  { key: 'used_bbq',      icon: '🔥', label: '바베큐 이용' },
  { key: 'used_firewood', icon: '🪵', label: '장작 이용' },
  { key: 'used_pool',     icon: '🏊', label: '수영장/온수풀 이용' },
];

function CheckoutBehaviorRecorder({ name, phone, customers, onSave, onSkip }) {
  const [selections, setSelections] = useState({
    used_bbq: null, used_firewood: null, used_pool: null,
  });

  const customer = customers.find(c => c.phone === phone);
  const selectedCount = Object.values(selections).filter(v => v !== null).length;

  function toggle(key, value) {
    setSelections(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
  }

  function handleSave() {
    if (!customer) { onSkip(); return; }
    const data = {};
    BEHAVIOR_ITEMS.forEach(({ key }) => {
      if (selections[key] !== null) data[key] = selections[key];
    });
    onSave(customer.id, data);
  }

  return (
    <>
      {/* 딤 배경 */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.5)' }} onClick={onSkip} />

      {/* 모달 */}
      <div style={{
        position: 'fixed',
        top: '10%', left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 40px)',
        maxWidth: 380,
        maxHeight: '80vh',
        zIndex: 301,
        background: 'white',
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        boxShadow: '0 12px 48px rgba(0,0,0,0.22)',
      }}>
        {/* 헤더 */}
        <div style={{ background: 'var(--primary)', padding: '20px 20px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>👋</div>
          <div style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>방금 퇴실한 고객 기록</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 4 }}>
            <strong style={{ color: 'white' }}>{name}</strong>님이 이용한 서비스를 기록해두세요
          </div>
        </div>

        {/* 선택 영역 */}
        <div style={{ padding: '16px 16px 4px' }}>
          {BEHAVIOR_ITEMS.map(({ key, icon, label }) => (
            <div key={key} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 4px',
              borderBottom: '1px solid var(--border-light)',
            }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {icon} {label}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => toggle(key, true)}
                  style={{
                    padding: '7px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700,
                    background: selections[key] === true ? 'var(--green)' : 'var(--border-light)',
                    color: selections[key] === true ? 'white' : 'var(--text-secondary)',
                    transition: 'all 0.12s',
                  }}
                >
                  ✔ 예
                </button>
                <button
                  onClick={() => toggle(key, false)}
                  style={{
                    padding: '7px 18px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700,
                    background: selections[key] === false ? '#fee2e2' : 'var(--border-light)',
                    color: selections[key] === false ? '#b91c1c' : 'var(--text-secondary)',
                    transition: 'all 0.12s',
                  }}
                >
                  ✖ 아니오
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* 버튼 */}
        <div style={{ padding: '14px 16px 20px', display: 'flex', gap: 8 }}>
          <button
            onClick={onSkip}
            style={{
              flex: 1, padding: '12px', borderRadius: 12, border: '1px solid var(--border)',
              background: 'white', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >
            나중에
          </button>
          <button
            onClick={handleSave}
            style={{
              flex: 2, padding: '12px', borderRadius: 12, border: 'none',
              background: 'var(--green)',
              color: 'white',
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.12s',
            }}
          >
            {selectedCount > 0 ? `저장 (${selectedCount}/3)` : '저장'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── 메시지 종류 카드 ───
function TemplateCard({ template, isSelected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '14px 12px',
        borderRadius: 'var(--radius)',
        border: `2px solid ${isSelected ? 'var(--green)' : 'var(--border)'}`,
        background: isSelected ? '#f0fdf4' : 'white',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.14s',
        boxShadow: isSelected ? '0 0 0 3px rgba(34,197,94,0.15)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 24, marginBottom: 6 }}>{template.icon}</div>
      <div style={{
        fontSize: 13, fontWeight: 700, lineHeight: 1.3,
        color: isSelected ? 'var(--green-dark)' : 'var(--text-primary)',
        marginBottom: 3,
      }}>
        {template.name}
      </div>
      <div style={{
        fontSize: 11, color: isSelected ? 'var(--green-dark)' : 'var(--text-muted)',
        lineHeight: 1.4, opacity: 0.85,
      }}>
        {template.desc}
      </div>
    </div>
  );
}

export default function SendTab({ prefillCustomer, onClearPrefill }) {
  const [mode, setMode] = useState('paste');
  const [pasteText, setPasteText] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [formData, setFormData] = useState({
    name: '', phone: '', checkin: '', checkout: '', guests: '', channel: '',
  });
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('checkin');
  const [messageText, setMessageText] = useState('');
  const [messageEdited, setMessageEdited] = useState(false); // 수동 편집 여부
  const [sending, setSending] = useState(false);
  const [behaviorTarget, setBehaviorTarget] = useState(null); // { name, phone }
  const [conflictDialog, setConflictDialog] = useState(null); // { conflicts, onConfirm }
  const originalParsedRef = useRef(null); // OTA 파싱 원본 (수정 전)

  const { state, addCustomer, addReservation, updateCustomer, logMessage, showToast, markCheckinSent, markCheckoutSent } = useApp();
  const { rooms, roomTemplates, commonTemplates, user } = state;

  const selectedRoom = rooms.find(r => r.id === selectedRoomId) || null;

  // ─── 메시지 자동 생성 ───
  const generateMessage = useCallback((templateId, room, data) => {
    if (!room) return '';
    // 공통 템플릿(예약확인·입금확인·리뷰요청)은 commonTemplates에서, 나머지는 roomTemplates에서 참조
    const COMMON_IDS = ['reservation', 'payment', 'review'];
    const customTmpl = COMMON_IDS.includes(templateId)
      ? (commonTemplates?.[templateId] ?? null)
      : (roomTemplates?.[room.id]?.[templateId] ?? null);
    const enrichedData = {
      ...data,
      pensionName: user?.pensionName || '',
      pensionPhone: user?.phone || '',
    };
    return buildMessage(templateId, room, enrichedData, customTmpl);
  }, [roomTemplates, commonTemplates, user?.pensionName, user?.phone]); // eslint-disable-line

  // ─── 고객 탭에서 이동 시 자동 채움 ───
  useEffect(() => {
    if (prefillCustomer) {
      setMode('manual');
      setFormData({
        name: prefillCustomer.name || '',
        phone: prefillCustomer.phone || '',
        checkin: prefillCustomer.checkin || '',
        checkout: prefillCustomer.checkout || '',
        guests: String(prefillCustomer.guests || ''),
        channel: prefillCustomer.channel || '',
      });
      setSelectedRoomId(prefillCustomer.roomId || '');
      setMessageText('');
      setMessageEdited(false);
      onClearPrefill?.();
    }
  }, [prefillCustomer, onClearPrefill]);

  // ─── 메시지 종류 선택 ───
  function handleTemplateSelect(templateId) {
    setSelectedTemplate(templateId);
    // 객실이 선택된 경우 즉시 메시지 생성 (편집 여부 무관 — 사용자가 명시적으로 선택한 것)
    if (selectedRoom) {
      const msg = generateMessage(templateId, selectedRoom, {
        name: formData.name || '고객',
        checkin: formData.checkin,
        checkout: formData.checkout,
      });
      setMessageText(msg);
      setMessageEdited(false);
    }
  }

  // ─── 객실 선택 ───
  function handleRoomSelect(roomId) {
    const room = rooms.find(r => r.id === roomId) || null;
    setSelectedRoomId(roomId);
    // 메시지를 편집하지 않았거나 메시지가 비어있으면 자동 생성
    if (!messageEdited || !messageText.trim()) {
      const msg = generateMessage(selectedTemplate, room, {
        name: formData.name || '고객',
        checkin: formData.checkin,
        checkout: formData.checkout,
      });
      setMessageText(msg);
      setMessageEdited(false);
    }
  }

  // ─── 메시지 재생성 (버튼 클릭) ───
  function regenerateMessage() {
    if (!selectedRoom) return;
    const msg = generateMessage(selectedTemplate, selectedRoom, {
      name: formData.name || '고객',
      checkin: formData.checkin,
      checkout: formData.checkout,
    });
    setMessageText(msg);
    setMessageEdited(false);
  }

  // ─── 초기화 ───
  function handleReset() {
    if (!window.confirm('입력된 내용을 모두 지우시겠습니까?')) return;
    setPasteText('');
    setParsedData(null);
    setFormData({ name: '', phone: '', checkin: '', checkout: '', guests: '', channel: '' });
    setSelectedRoomId('');
    setMessageText('');
    setMessageEdited(false);
  }

  // ─── OTA 파싱 ───
  function handleParse() {
    if (!pasteText.trim()) return;
    const result = parseOTAText(pasteText);
    originalParsedRef.current = {
      name: result.name || '',
      phone: result.phone || '',
      checkin: result.checkin || '',
      checkout: result.checkout || '',
      guests: result.guests || '',
      room: result.room || '',
      channel: result.channel || '',
    };
    setParsedData(result);
    const newFormData = {
      name: result.name || '',
      phone: result.phone || '',
      checkin: result.checkin || '',
      checkout: result.checkout || '',
      guests: result.guests || '',
      channel: result.channel || '',
    };
    setFormData(newFormData);

    // 객실 자동 매칭
    let matchedRoom = null;
    if (result.room && rooms.length > 0) {
      const norm = result.room.toLowerCase().replace(/\s/g, '');
      matchedRoom = rooms.find(r => {
        const rn = r.name.toLowerCase().replace(/\s/g, '');
        return rn === norm || rn.includes(norm) || norm.includes(rn);
      }) || null;
      if (matchedRoom) setSelectedRoomId(matchedRoom.id);
    }

    // 객실 매칭 됐으면 메시지도 자동 생성
    if (matchedRoom) {
      const msg = generateMessage(selectedTemplate, matchedRoom, {
        name: result.name || '고객',
        checkin: result.checkin,
        checkout: result.checkout,
      });
      setMessageText(msg);
      setMessageEdited(false);
    }
  }

  function updateParsedField(key, value) {
    setParsedData(prev => prev ? { ...prev, [key]: value } : prev);
    setFormData(prev => ({ ...prev, [key]: value }));
  }

  // ─── 중복 예약 체크 ───
  function checkConflict(roomId, checkin, checkout) {
    if (!roomId || !checkin || !checkout) return [];
    return state.reservations.filter(r =>
      r.status !== 'cancelled' &&
      r.roomId === roomId &&
      r.checkin && r.checkout &&
      checkin < r.checkout &&
      checkout > r.checkin
    );
  }

  // ─── 발송 처리 (실제 실행) ───
  async function executeSend() {
    if (user?.isAnonymous) {
      showToast('체험 계정에서는 실제 문자를 발송할 수 없습니다.\n회원가입 후 이용해주세요.', 'error');
      return;
    }
    setSending(true);
    try {
      await sendMessage({
        phone: formData.phone,
        templateId: selectedTemplate,
        room: selectedRoom,
        data: { name: formData.name, checkin: formData.checkin, checkout: formData.checkout },
        rawText: messageText, // 편집된 내용 그대로 발송
      });

      // 예약 저장 (예약확인 or 체크인 안내 발송 시)
      if (selectedTemplate === 'reservation' || selectedTemplate === 'checkin') {
        const existing = state.reservations.find(
          r => r.phone === formData.phone && r.checkin === formData.checkin
        );
        if (!existing && formData.checkin) {
          addReservation({
            id: `res_${Date.now()}`,
            customerId: null,
            customerName: formData.name,
            phone: formData.phone,
            roomId: selectedRoomId,
            roomName: selectedRoom?.name || '',
            checkin: formData.checkin,
            checkout: formData.checkout || '',
            guests: parseInt(formData.guests) || 0,
            channel: formData.channel || '',
            checkinSent: selectedTemplate === 'checkin',
            checkoutSent: false,
          });
        }
      }

      // 발송 상태 업데이트 — 전화번호로 예약 찾아서 sent 플래그 갱신
      const findRes = () => {
        // 체크인/매너타임: 전화번호 + 체크인 날짜로 매칭
        if (selectedTemplate === 'checkin' || selectedTemplate === 'mannertime') {
          if (formData.checkin) {
            return state.reservations.find(
              r => r.status !== 'cancelled' && r.phone === formData.phone && r.checkin === formData.checkin
            );
          }
        }
        // 체크아웃: 전화번호 + 체크아웃 날짜로 매칭
        if (selectedTemplate === 'checkout') {
          if (formData.checkout) {
            return state.reservations.find(
              r => r.status !== 'cancelled' && r.phone === formData.phone && r.checkout === formData.checkout
            );
          }
          // 체크아웃 날짜가 없으면 해당 전화번호의 가장 최근 예약으로 폴백
          const matches = state.reservations.filter(
            r => r.status !== 'cancelled' && r.phone === formData.phone
          );
          return matches.sort((a, b) => (b.checkin || '').localeCompare(a.checkin || ''))[0] || null;
        }
        return null;
      };

      const targetRes = findRes();
      if (targetRes) {
        if (selectedTemplate === 'checkin' || selectedTemplate === 'mannertime') {
          markCheckinSent(targetRes.id);
        } else if (selectedTemplate === 'checkout') {
          markCheckoutSent([targetRes.id]);
        }
      }

      // 고객 DB 저장
      addCustomer({
        name: formData.name,
        phone: formData.phone,
        roomId: selectedRoomId,
        roomName: selectedRoom?.name || '',
        guests: parseInt(formData.guests) || 0,
        channel: formData.channel || '',
        firstVisit: formData.checkin || new Date().toISOString().split('T')[0],
        lastVisit: formData.checkin || new Date().toISOString().split('T')[0],
      });

      // 발송 이력 저장
      logMessage({
        customerName: formData.name,
        phone: formData.phone,
        templateId: selectedTemplate,
        templateName: MESSAGE_TEMPLATES[selectedTemplate]?.name || '',
        roomId: selectedRoomId,
        messageText: messageText,
      });

      showToast(`${formData.name}님께 ${MESSAGE_TEMPLATES[selectedTemplate]?.name} 메시지를 보냈습니다 ✓`);

      // 퇴실 메시지 발송 시 → 행동 기록 모달 표시
      if (selectedTemplate === 'checkout') {
        setBehaviorTarget({ name: formData.name, phone: formData.phone });
      }

      // OTA 파싱 로그 기록 (파싱을 통해 입력된 경우만)
      if (originalParsedRef.current && user?.uid && !user?.isAnonymous) {
        const orig = originalParsedRef.current;
        const FIELDS = ['name', 'phone', 'checkin', 'checkout', 'guests'];
        const editedFields = FIELDS.filter(f => (orig[f] || '') !== (formData[f] || ''));
        fsLogParsingEvent(user.uid, {
          channel: orig.channel || '기타',
          original: { name: orig.name, phone: orig.phone, checkin: orig.checkin, checkout: orig.checkout, guests: orig.guests, room: orig.room },
          submitted: { name: formData.name, phone: formData.phone, checkin: formData.checkin, checkout: formData.checkout, guests: formData.guests },
          editedFields,
        }).catch(() => {});
        originalParsedRef.current = null;
      }

      // 폼 초기화
      setPasteText('');
      setParsedData(null);
      setFormData({ name: '', phone: '', checkin: '', checkout: '', guests: '', channel: '' });
      setSelectedRoomId('');
      setMessageText('');
      setMessageEdited(false);

    } catch (e) {
      showToast('메시지 발송 중 오류가 발생했습니다', 'error');
    } finally {
      setSending(false);
    }
  }

  // ─── 발송 버튼 클릭 (충돌 체크 후 executeSend) ───
  async function handleSend() {
    if (!formData.phone) { showToast('전화번호를 입력해주세요', 'error'); return; }
    if (!/^(01[0-9]|050)\d{7,8}$/.test(formData.phone.replace(/[-\s]/g, ''))) {
      showToast('올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)', 'error'); return;
    }
    if (!formData.name) { showToast('이름을 입력해주세요', 'error'); return; }
    if (!selectedRoomId) { showToast('객실을 선택해주세요', 'error'); return; }
    if (!messageText.trim()) { showToast('메시지 내용을 입력해주세요', 'error'); return; }
    if (formData.checkin && formData.checkout && formData.checkout <= formData.checkin) {
      showToast('체크아웃은 체크인 이후 날짜여야 합니다', 'error'); return;
    }

    // 중복 예약 체크 (예약확인·체크인 안내 발송 시)
    if ((selectedTemplate === 'reservation' || selectedTemplate === 'checkin') && formData.checkin && formData.checkout) {
      const conflicts = checkConflict(selectedRoomId, formData.checkin, formData.checkout);
      if (conflicts.length > 0) {
        setConflictDialog({ conflicts, onConfirm: () => { setConflictDialog(null); executeSend(); } });
        return;
      }
    }

    await executeSend();
  }

  const showForm = parsedData || mode === 'manual' || (mode === 'screenshot' && !!parsedData);
  const canSend = formData.phone && formData.name && selectedRoomId && messageText.trim();

  return (
    <div className="tab-content">
      {/* ─── 중복 예약 경고 다이얼로그 ─── */}
      {conflictDialog && (
        <>
          <div
            onClick={() => setConflictDialog(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }}
          />
          <div style={{
            position: 'fixed',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)',
            maxWidth: 340,
            background: 'white',
            borderRadius: 20,
            zIndex: 401,
            padding: '24px 20px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 16, textAlign: 'center', marginBottom: 6 }}>
              같은 객실에 이미 예약이 있습니다
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 14 }}>
              날짜가 겹치는 기존 예약을 확인하세요
            </div>
            {conflictDialog.conflicts.map(r => (
              <div key={r.id} style={{
                background: '#fef2f2', borderRadius: 10,
                padding: '10px 14px', marginBottom: 8,
                border: '1px solid #fca5a5',
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626' }}>{r.roomName}</div>
                <div style={{ fontSize: 12, color: '#991b1b', marginTop: 3 }}>
                  {r.customerName} · {r.checkin} ~ {r.checkout}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setConflictDialog(null)}
                style={{
                  flex: 1, padding: '12px', borderRadius: 12,
                  border: '1px solid var(--border)', background: 'white',
                  fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={conflictDialog.onConfirm}
                style={{
                  flex: 1, padding: '12px', borderRadius: 12,
                  border: 'none', background: '#dc2626',
                  fontSize: 14, fontWeight: 700, color: 'white', cursor: 'pointer',
                }}
              >
                그래도 저장
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── 퇴실 후 행동 기록 모달 ─── */}
      {behaviorTarget && (
        <CheckoutBehaviorRecorder
          name={behaviorTarget.name}
          phone={behaviorTarget.phone}
          customers={state.customers}
          onSave={(customerId, data) => {
            updateCustomer({ id: customerId, ...data });
            setBehaviorTarget(null);
            showToast('이용 정보가 저장되었습니다');
          }}
          onSkip={() => setBehaviorTarget(null)}
        />
      )}
      {/* ─── 모드 토글 ─── */}
      <div className="mode-toggle" style={{ marginBottom: 16 }}>
        <button className={`mode-btn ${mode === 'paste' ? 'active' : ''}`} onClick={() => setMode('paste')}>
          📋 문자 붙여넣기
        </button>
        <button className={`mode-btn ${mode === 'manual' ? 'active' : ''}`} onClick={() => setMode('manual')}>
          ✏️ 직접 입력
        </button>
        <button className={`mode-btn ${mode === 'screenshot' ? 'active' : ''}`} onClick={() => setMode('screenshot')}>
          🖼️ 스크린샷
        </button>
      </div>

      {/* ═══ 붙여넣기 모드 ═══ */}
      {mode === 'paste' && (
        <div>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <textarea
              className="input"
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder="예약 문자 붙여넣으면 자동으로 입력됩니다&#10;야놀자 / 여기어때 / 네이버 예약 문자 그대로 붙여넣으세요"
              style={{ minHeight: 140, resize: 'none', border: '2px dashed var(--green-border)', lineHeight: 1.6, fontSize: 14, fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className="btn btn-outline" style={{ flex: 1, padding: '11px', fontSize: 13 }} onClick={() => setPasteText(SAMPLE_OTA_TEXT)}>
              예시 보기
            </button>
            <button className="btn btn-green" style={{ flex: 2, padding: '11px' }} onClick={handleParse} disabled={!pasteText.trim()}>
              👉 예약 정보 자동 입력하기
            </button>
          </div>

          {parsedData && (
            <div className="parse-result-card" style={{ marginBottom: 16 }}>
              <div className="parse-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--green-dark)' }}>
                    {isParseSuccess(parsedData) ? '정보 입력 완료 ✓' : '일부 정보를 확인해주세요'}
                  </span>
                  {parsedData.channel && <span className="badge badge-green">{parsedData.channel}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>탭해서 수정</span>
                  <button
                    onClick={handleReset}
                    style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 10px',
                      borderRadius: 6, border: '1px solid #fca5a5',
                      background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                      lineHeight: 1.6,
                    }}
                  >
                    초기화
                  </button>
                </div>
              </div>
              <EditableField label="이름" value={parsedData.name} onChange={v => updateParsedField('name', v)} confidence={parsedData.confidence?.name} placeholder="예약자명" />
              <EditableField label="전화번호" value={parsedData.phone} onChange={v => updateParsedField('phone', v)} confidence={parsedData.confidence?.phone} type="tel" placeholder="010-0000-0000" />
              <EditableField label="체크인" value={parsedData.checkin} onChange={v => updateParsedField('checkin', v)} confidence={parsedData.confidence?.checkin} type="date" placeholder="체크인 날짜" />
              <EditableField label="체크아웃" value={parsedData.checkout} onChange={v => updateParsedField('checkout', v)} confidence={parsedData.confidence?.checkout} type="date" placeholder="체크아웃 날짜" />
              <EditableField label="인원" value={parsedData.guests} onChange={v => updateParsedField('guests', v)} confidence={parsedData.confidence?.guests} placeholder="인원 수" />
              {parsedData.room && (
                <EditableField label="객실(원문)" value={parsedData.room} onChange={v => updateParsedField('room', v)} confidence={parsedData.confidence?.room} placeholder="객실명" />
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ 스크린샷 업로드 모드 ═══ */}
      {mode === 'screenshot' && !parsedData && (
        <ScreenshotUploader
          onParsed={(result, formValues) => {
            setParsedData(result);
            setFormData(formValues);
            const norm = result.room?.toLowerCase().replace(/\s/g, '');
            if (norm) {
              const matched = rooms.find(r => {
                const rn = r.name.toLowerCase().replace(/\s/g, '');
                return rn === norm || rn.includes(norm) || norm.includes(rn);
              });
              if (matched) {
                setSelectedRoomId(matched.id);
                const msg = generateMessage(selectedTemplate, matched, {
                  name: result.name || '고객',
                  checkin: result.checkin,
                  checkout: result.checkout,
                });
                setMessageText(msg);
                setMessageEdited(false);
              }
            }
          }}
        />
      )}

      {/* ═══ 직접 입력 모드 ═══ */}
      {mode === 'manual' && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>직접 입력</span>
            <button
              onClick={handleReset}
              style={{
                fontSize: 12, fontWeight: 500, padding: '4px 12px',
                borderRadius: 8, border: '1px solid var(--border)',
                background: 'white', color: 'var(--text-muted)', cursor: 'pointer',
                lineHeight: 1.5,
              }}
            >
              초기화
            </button>
          </div>
          <div className="card">
            <div className="input-group">
              <label className="input-label">이름 *</label>
              <input className="input" placeholder="예약자명" value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">전화번호 *</label>
              <input className="input" placeholder="010-0000-0000" type="tel" value={formData.phone}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
              {is050Number(formData.phone) && (
                <div style={{ marginTop: 4 }}><span className="safe-number-badge">🔒 050 안심번호입니다</span></div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="input-group">
                <label className="input-label">체크인</label>
                <input className="input" type="date" value={formData.checkin}
                  onChange={e => setFormData(p => ({ ...p, checkin: e.target.value }))} />
              </div>
              <div className="input-group">
                <label className="input-label">체크아웃</label>
                <input className="input" type="date" value={formData.checkout}
                  onChange={e => setFormData(p => ({ ...p, checkout: e.target.value }))} />
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">인원</label>
              <input className="input" placeholder="명" type="number" value={formData.guests}
                onChange={e => setFormData(p => ({ ...p, guests: e.target.value }))} />
            </div>
          </div>
        </div>
      )}

      {/* ─── 객실 선택 ─── */}
      {showForm && (
        <>
          <p className="section-title" style={{ marginTop: 4 }}>객실 선택</p>
          {rooms.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: '16px' }}>
              객실이 없습니다. 설정에서 객실을 추가해주세요.
            </div>
          ) : (
            <div className="room-grid" style={{ marginBottom: 16 }}>
              {rooms.map(room => (
                <div
                  key={room.id}
                  className={`room-card ${selectedRoomId === room.id ? 'selected' : ''}`}
                  onClick={() => handleRoomSelect(room.id)}
                >
                  <div className="room-name">{room.name}</div>
                  <div className="room-capacity">최대 {room.capacity}명</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {room.checkinTime} → {room.checkoutTime}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── 메시지 종류 선택 ─── */}
      {showForm && (
        <>
          <p className="section-title">메시지 종류</p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            marginBottom: 16,
          }}>
            {OPERATION_TEMPLATE_IDS.map(id => {
              const t = MESSAGE_TEMPLATES[id];
              if (!t) return null;
              return (
                <TemplateCard
                  key={id}
                  template={t}
                  isSelected={selectedTemplate === id}
                  onClick={() => handleTemplateSelect(id)}
                />
              );
            })}
          </div>

          {/* ─── 메시지 작성 영역 ─── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <p className="section-title" style={{ margin: 0 }}>메시지 내용</p>
            {messageEdited && (
              <button
                onClick={regenerateMessage}
                disabled={!selectedRoom}
                style={{
                  fontSize: 11, color: 'var(--green-dark)', fontWeight: 600,
                  background: 'var(--green-light)', border: 'none',
                  borderRadius: 8, padding: '4px 10px', cursor: 'pointer',
                }}
              >
                ↺ 다시 생성
              </button>
            )}
          </div>

          <textarea
            className="input"
            value={messageText}
            onChange={e => {
              setMessageText(e.target.value);
              setMessageEdited(true);
            }}
            placeholder={selectedRoom ? '메시지 종류를 선택하면 자동으로 작성됩니다' : '객실을 먼저 선택해주세요'}
            style={{
              minHeight: 180, resize: 'vertical',
              fontSize: 13, lineHeight: 1.75,
              fontFamily: 'inherit',
              marginBottom: 4,
            }}
          />
          {messageText && !messageEdited && (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, marginTop: 2 }}>
              메시지 종류 카드를 다시 클릭하면 새로 생성됩니다
            </p>
          )}
          {messageEdited && (
            <p style={{ fontSize: 11, color: 'var(--amber)', marginBottom: 12, marginTop: 2 }}>
              직접 수정된 내용으로 발송됩니다
            </p>
          )}
          {!messageText && (
            <div style={{ height: 12 }} />
          )}

          {/* ─── 메시지 보내기 버튼 ─── */}
          <button
            className="btn btn-green btn-block"
            style={{ padding: '16px', fontSize: 16, borderRadius: 'var(--radius)' }}
            onClick={handleSend}
            disabled={sending || !canSend}
          >
            {sending ? (
              <><div className="spinner" /> 발송 중...</>
            ) : (
              <>💬 {formData.name ? `${formData.name}님께 ` : ''}메시지 보내기</>
            )}
          </button>

          {!canSend && (
            <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              {[!formData.name && '이름', !formData.phone && '전화번호', !selectedRoomId && '객실', !messageText.trim() && '메시지 내용']
                .filter(Boolean).join(', ')}
              {' '}을(를) 확인해주세요
            </p>
          )}
        </>
      )}

      {/* 초기 상태 */}
      {!parsedData && mode === 'paste' && !pasteText && (
        <div className="empty-state" style={{ paddingTop: 32 }}>
          <div className="icon">📋</div>
          <p style={{ fontWeight: 700 }}>예약 문자 붙여넣으면 자동으로 입력됩니다</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            복사해서 넣으면 이름, 날짜, 객실이<br />
            자동으로 입력됩니다
          </p>
        </div>
      )}
    </div>
  );
}
